import { Icon } from '@aqarmap/aqarmap-ui';
import { useEffect, useState } from 'react';
import { Address, Attributes, ListingPrice } from '~/components/ListingCard';
import { useTranslation } from 'react-i18next';
import {
  SavedPropertyItemType,
  SavedPropertyListingType,
} from '~/types/activitiesTypes';
import {
  getSavedPropertyAddress,
  getSavedPropertyImage,
  getSavedPropertyListingRoute,
} from '~/modules/activities/pages/Favorites/utils';
import ListingMediaIndicator from '~activities/pages/Favorites/ListingMediaIndicator';
import { PaymentMethodEnum, SectionsEnum } from '~/types/listingTypes';
import ListingInstallmentsPlan from '~/modules/activities/pages/Favorites/ListingInstallmentsPlan';
import ListingRentalPlan from '~/modules/activities/pages/Favorites/ListingRentalPlan';
import getRentalPlanDetails from '~/types/getRentalPlanDetails';
import ListingNote from '~activities/pages/Favorites/ListingNote';
import ActionMenu from '~activities/pages/Favorites/ActionMenu';

type SavedPropertyCardProps = {
  item: SavedPropertyItemType;
  handelDeleteFavorite: () => void;
  handleOpenNoteModal: () => void;
  handleDeleteNote: () => void;
  isSheared?: boolean;
  setShareRoute: React.Dispatch<React.SetStateAction<string>>;
};

export default function FavoriteCard({
  item,
  handelDeleteFavorite,
  handleOpenNoteModal,
  handleDeleteNote,
  isSheared,
  setShareRoute,
}: SavedPropertyCardProps) {
  const { i18n } = useTranslation('app');
  const locale = i18n.language;
  const listing: SavedPropertyListingType = item.listing;
  const listingRoute = getSavedPropertyListingRoute(listing, locale);
  const listingImage = getSavedPropertyImage(listing);
  const listingAddress = getSavedPropertyAddress(listing);
  const [note, setNote] = useState(item.note || '');

  const hasInstallments =
    listing.section?.slug === SectionsEnum.FOR_SALE &&
    listing.payment_method !== PaymentMethodEnum.CASH &&
    Array.isArray(listing.payment_plans) &&
    listing.payment_plans.length > 0;

  const rentalPlanDetails = getRentalPlanDetails({
    plans: listing?.payment_plans,
    section: listing.section.slug,
  });

  useEffect(() => {
    setNote(item.note || '');
  }, [item.note]);

  return (
    <article className="relative flex flex-col md:flex-row md:min-h-[238px] rounded-[16px] border border-gray bg-white">
      <div
        className={`${locale === 'en' ? 'md:!rounded-tr-none' : 'md:!rounded-tl-none'} relative md:w-[308px] h-[192px] md:h-full bg-gray_light_1 rounded-ss-[16px] rounded-se-[16px] md:rounded-t-0 md:rounded-s-[16px]`}
      >
        <a
          href={listingRoute}
          target="_blank"
          rel="noreferrer"
          className={`block h-full w-full overflow-hidden ${locale === 'en' ? 'md:!rounded-tr-none' : 'md:!rounded-tl-none'} rounded-ss-[16px] rounded-se-[16px] md:rounded-t-0 md:rounded-s-[16px]`}
        >
          {listingImage ? (
            <img
              src={listingImage}
              alt={listing.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray__dark_1">
              <Icon name="image-placeholder" size="56px" />
            </div>
          )}
        </a>

        <ListingMediaIndicator
          photos={listing.photos.length}
          videoURL={listing.video_url ?? ''}
          className="absolute bottom-1.5x start-1.5x"
        />
        {listing?.user?.logo?.thumbnails?.small && (
          <span className="absolute bottom-[-26px] md:bottom-[calc(100%-52px)] overflow-hidden end-2x md:end-1.5x block bg-white px-1.5x py-[6px] text-caption-2 text-gray__dark_1 w-[56px] h-[56px] md:w-[40px] md:h-[40px] rounded-full md:rounded-[8px] border border-gray">
            <img
              src={listing?.user?.logo?.thumbnails?.small ?? ''}
              className="absolute top-0 left-0 w-full h-full "
            />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-2x md:p-3x">
        <div className="flex flex-col gap-2x justify-between h-full">
          <div className="flex flex-col gap-2x">
            <div className="flex items-start justify-between gap-2x">
              <div className="flex flex-col gap-x">
                <ListingPrice
                  price={listing.price || 0}
                  area={listing?.area || 0}
                  section={listing.section.slug}
                  showPricePerMeter={true}
                />
                {hasInstallments ? (
                  listing.payment_plans
                    ?.filter(
                      (plan) =>
                        plan.down_payment && Number(plan.down_payment) !== 0,
                    )
                    .map((plan) => {
                      return (
                        <ListingInstallmentsPlan
                          key={`installment-plan-${plan.id}`}
                          plan={plan}
                          locale={locale}
                        />
                      );
                    })
                ) : rentalPlanDetails?.hasRentalPlans ? (
                  <ListingRentalPlan payment_plans={listing.payment_plans} />
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-x">
              {listingAddress ? <Address address={listingAddress} /> : null}
              <Attributes
                listing={{
                  area: listing.area ?? 0,
                  attributes: {
                    rooms: listing.attributes?.rooms
                      ? Number(listing.attributes.rooms)
                      : 0,
                    baths: listing.attributes?.baths
                      ? Number(listing.attributes.baths)
                      : 0,
                  },
                }}
              />
            </div>
          </div>
          <ListingNote
            noteText={note}
            handleOpenNoteModal={handleOpenNoteModal}
            isSheared={isSheared}
          />
        </div>
      </div>
      <ActionMenu
        handelDeleteFavorite={handelDeleteFavorite}
        handleDeleteNote={handleDeleteNote}
        isSheared={isSheared}
        listingData={item}
        setShareRoute={setShareRoute}
      />
    </article>
  );
}
